<script setup lang="ts">
import { ref, onMounted, onUnmounted } from 'vue'
import { toTypedSchema } from '@/lib/zodTypedSchema'
import { AlertCircle } from 'lucide-vue-next'
import parsePhoneNumber from 'libphonenumber-js'
import { useAuth } from '@/composables/useAuth'
import {
  phoneVerificationSchema,
  type PhoneVerificationFormData
} from '@/utils/auth/validation'
import { Form, FormField, FormItem, FormControl, FormLabel, FormMessage } from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { PhoneInput } from '@/components/ui/phone-input'

const emit = defineEmits<{
  success: [token: string]
  error: [message: string]
}>()

const { setupRecaptcha, signInWithPhone, verifyPhoneCode, cleanupRecaptcha, isLoading, error } = useAuth()

// Two-step flow state
const step = ref<'phone' | 'code'>('phone')
const phoneNumber = ref('') // Display format (e.g., "+380 50 123 4567")
const phoneNumberE164 = ref('') // E.164 format for Firebase (e.g., "+380501234567")
const formError = ref<string | null>(null)

// Schemas for each step - removed, will validate manually
// const phoneSchema = toTypedSchema(phoneSignInSchema)
const codeSchema = toTypedSchema(phoneVerificationSchema)

/**
 * Step 1: Send SMS verification code
 * Validates phone number and converts to E.164 format
 */
const onPhoneSubmit = async (values: any) => {
  console.log('[PhoneAuthForm] Received phone value:', values.phoneNumber)

  // Clear any previous errors
  formError.value = null

  try {
    // Parse and validate phone number using libphonenumber-js
    const phoneNumberParsed = parsePhoneNumber(values.phoneNumber)

    if (!phoneNumberParsed || !phoneNumberParsed.isValid()) {
      formError.value = 'Invalid or incomplete phone number'
      emit('error', formError.value)
      return
    }

    // Convert to E.164 format for Firebase
    const e164Number = phoneNumberParsed.number
    console.log('[PhoneAuthForm] Sending SMS to E.164:', e164Number)

    // Send SMS with E.164 format
    const success = await signInWithPhone(e164Number)

    if (success) {
      console.log('[PhoneAuthForm] SMS sent successfully')
      // Store both E.164 (for resend) and formatted display number
      phoneNumberE164.value = e164Number
      phoneNumber.value = phoneNumberParsed.formatInternational()
      step.value = 'code'
      formError.value = null
    } else {
      console.error('[PhoneAuthForm] Failed to send SMS:', error.value)
      formError.value = error.value || 'Failed to send verification code'
      emit('error', formError.value)
    }
  } catch (err) {
    console.error('[PhoneAuthForm] Phone validation error:', err)
    formError.value = 'Invalid phone number format'
    emit('error', formError.value)
  }
}

/**
 * Step 2: Verify code and complete authentication
 */
const onCodeSubmit = async (values: Record<string, any>) => {
  const typedValues = values as PhoneVerificationFormData
  console.log('[PhoneAuthForm] Verifying code')

  // Clear any previous errors
  formError.value = null

  const token = await verifyPhoneCode(typedValues.code)

  if (token) {
    console.log('[PhoneAuthForm] Authentication successful, token received')
    formError.value = null
    emit('success', token)
  } else {
    console.error('[PhoneAuthForm] Verification failed:', error.value)
    // Set form error for inline display
    formError.value = error.value || 'Invalid verification code'
    // Also emit error event
    emit('error', formError.value)
  }
}

/**
 * Go back to phone number step
 */
const backToPhone = () => {
  step.value = 'phone'
  formError.value = null
}

/**
 * Resend verification code
 */
const resendCode = async () => {
  if (!phoneNumberE164.value) return

  formError.value = null
  const success = await signInWithPhone(phoneNumberE164.value)

  if (!success) {
    formError.value = error.value || 'Failed to resend verification code'
    emit('error', formError.value)
  }
}

// Setup recaptcha on mount
onMounted(() => {
  setupRecaptcha('recaptcha-container', 'invisible')
})

// Cleanup on unmount
onUnmounted(() => {
  formError.value = null
  cleanupRecaptcha()
})
</script>

<template>
  <div class="space-y-4">
    <!-- Error Alert -->
    <Alert v-if="formError" variant="destructive">
      <AlertCircle class="h-4 w-4" />
      <AlertDescription>{{ formError }}</AlertDescription>
    </Alert>

    <!-- Step 1: Phone Number Input -->
    <Form
      v-if="step === 'phone'"
      @submit="onPhoneSubmit"
      class="space-y-4"
    >
      <!-- Phone Number Field -->
      <FormField name="phoneNumber" v-slot="{ componentField }">
        <FormItem>
          <FormLabel>Phone Number</FormLabel>
          <FormControl>
            <PhoneInput v-bind="componentField" />
          </FormControl>
        </FormItem>
      </FormField>

      <!-- Submit Button -->
      <Button
        type="submit"
        :disabled="isLoading"
        class="w-full h-12 text-base font-medium"
      >
        <span v-if="isLoading">Sending code...</span>
        <span v-else>Send verification code</span>
      </Button>
    </Form>

    <!-- Step 2: Verification Code Input -->
    <Form
      v-else
      :validation-schema="codeSchema"
      @submit="onCodeSubmit"
      class="space-y-4"
    >
      <!-- Info Text -->
      <div class="text-sm text-muted-foreground text-center">
        <p>Enter the 6-digit code sent to</p>
        <p class="font-medium text-foreground mt-1">{{ phoneNumber }}</p>
        <button
          type="button"
          @click="backToPhone"
          class="text-primary hover:text-primary/80 hover:underline mt-2"
        >
          Change number
        </button>
      </div>

      <!-- Verification Code Field -->
      <FormField name="code" v-slot="{ componentField }">
        <FormItem>
          <FormLabel>Verification Code</FormLabel>
          <FormControl>
            <Input
              type="text"
              inputmode="numeric"
              pattern="[0-9]*"
              maxlength="6"
              placeholder="000000"
              autocomplete="one-time-code"
              v-bind="componentField"
              class="text-center text-2xl tracking-widest"
            />
          </FormControl>
          <FormMessage />
        </FormItem>
      </FormField>

      <!-- Resend Code Button -->
      <div class="text-center">
        <button
          type="button"
          @click="resendCode"
          :disabled="isLoading"
          class="text-sm text-primary hover:text-primary/80 hover:underline disabled:opacity-50"
        >
          Resend code
        </button>
      </div>

      <!-- Submit Button -->
      <Button
        type="submit"
        :disabled="isLoading"
        class="w-full h-12 text-base font-medium"
      >
        <span v-if="isLoading">Verifying...</span>
        <span v-else>Verify and sign in</span>
      </Button>
    </Form>

    <!-- Recaptcha Container (invisible) -->
    <div id="recaptcha-container"></div>
  </div>
</template>
